/* Formatting shared by both SPAs. Money arrives from the API as integer minor units plus an ISO
   currency; it is shown as CA$1,234.50 / US$… with a true minus sign, like the prototype. */

export type Currency = "CAD" | "USD";

export interface Money {
  amount_minor: number;
  currency: Currency | string;
}

const SYM: Record<string, string> = { CAD: "CA$", USD: "US$" };
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format a major-unit amount (dollars), always with two decimals. */
export function formatAmount(amount: number, currency: string = "CAD"): string {
  const n = Number(amount) || 0;
  const sym = SYM[currency] ?? `${currency} `;
  const body = Math.abs(n).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (n < 0 ? "−" : "") + sym + body;
}

/** Format an API money value; null/undefined shows an em dash. */
export function formatMoney(m: Money | null | undefined): string {
  if (!m) return "—";
  return formatAmount(m.amount_minor / 100, m.currency);
}

/** Parse what a person typed ("1,234.5", "$99") into minor units; null when it isn't a number. */
export function toMinor(input: string | number): number | null {
  const s = String(input)
    .replace(/CA|US|[\s,$]/g, "")
    .replace("−", "-");
  if (s === "" || s === "-" || s === "." || !/^-?\d*(\.\d*)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/** Minor units to an editable string ("1234.50"). */
export function fromMinor(minor: number | null | undefined): string {
  if (minor === null || minor === undefined) return "";
  return (minor / 100).toFixed(2);
}

function parse(s: string): Date {
  return new Date(s.length === 10 ? `${s}T12:00:00` : s);
}

/** "Oct 23, 2026"; dates without a time are read as local noon so they never shift a day. */
export function formatDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = parse(s);
  if (Number.isNaN(d.getTime())) return "—";
  return `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Oct 23, 2026, 14:05" */
export function formatDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  const d = parse(s);
  if (Number.isNaN(d.getTime())) return "—";
  return `${formatDate(s)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** "just now", "5 min ago", "3 h ago", "yesterday", "4 d ago" */
export function formatRelative(s: string | null | undefined, now = Date.now()): string {
  if (!s) return "—";
  const m = (now - new Date(s).getTime()) / 6e4;
  if (m < 1) return "just now";
  if (m < 60) return `${Math.floor(m)} min ago`;
  if (m < 1440) return `${Math.floor(m / 60)} h ago`;
  const d = Math.floor(m / 1440);
  return d === 1 ? "yesterday" : `${d} d ago`;
}

/** Hours to "7.5 h". */
export function formatHours(h: number | string | null | undefined): string {
  const n = Number(h) || 0;
  return `${Math.round(n * 100) / 100} h`;
}

/** Minutes as decimal hours, "1.5" / "0.25" / "-0.25" (UX-139: hours are decimals everywhere, never
    h:mm). Time is logged in quarter hours, so this is normally .25, .5 or .75; add the unit yourself. */
export function formatMinutes(min: number): string {
  const n = Math.round(Number(min) || 0);
  return String(Math.round((n / 60) * 100) / 100);
}

/** Minutes rounded up to the next quarter hour, as time entries are logged (at least 15). */
export function quarterMinutes(min: number): number {
  return Math.max(15, Math.ceil(Math.max(min, 0) / 15) * 15);
}

/** Seconds to "01:02:03" for a running timer. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
    .map((x) => String(x).padStart(2, "0"))
    .join(":");
}

/** Values whose label isn't plain sentence case: the opportunity outcomes read as names. */
const LABELS: Record<string, string> = { closed_won: "Closed Won", closed_lost: "Closed Lost" };

/** "proposal" / "closed_won" / "in_progress" to "Proposal" / "Closed Won" / "In progress". */
export function humanize(s: string | null | undefined): string {
  if (!s) return "—";
  if (LABELS[s]) return LABELS[s];
  const t = s.replace(/[_-]+/g, " ").trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** A month from today as YYYY-MM-DD (the same day next month, or its last day when shorter):
    the expected close date a new opportunity starts with. */
export function monthFromTodayIso(now = new Date()): string {
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const last = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  return todayIso(new Date(next.getFullYear(), next.getMonth(), Math.min(now.getDate(), last)));
}

/** Today's date as YYYY-MM-DD in local time. */
export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** A task is overdue while it isn't done and its due date (YYYY-MM-DD) has passed. */
export const isTaskOverdue = (t: { status: string; due_date?: string | null }, today = todayIso()) =>
  t.status !== "done" && !!t.due_date && t.due_date < today;

/* ---------- business days: Monday to Friday, less holidays (YYYY-MM-DD) ---------- */

const isoDay = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};

/** `iso` moved by `days` calendar days. */
export const addDaysIso = (iso: string, days: number) => {
  const d = isoDay(iso);
  return todayIso(new Date(d.getFullYear(), d.getMonth(), d.getDate() + days));
};

export const isBusinessDay = (iso: string, holidays: ReadonlySet<string> = new Set()) => {
  const weekday = isoDay(iso).getDay();
  return weekday !== 0 && weekday !== 6 && !holidays.has(iso);
};

/** The day itself if it's a business day, else the next one (a Saturday becomes Monday). */
export function nextBusinessDay(iso: string, holidays: ReadonlySet<string> = new Set()): string {
  let day = iso;
  for (let i = 0; i < 400 && !isBusinessDay(day, holidays); i++) day = addDaysIso(day, 1);
  return day;
}

/** A library task's due date: `offsetDays` after `base`, on a business day. */
export const dueAfter = (base: string, offsetDays: number, holidays: ReadonlySet<string> = new Set()) =>
  nextBusinessDay(addDaysIso(base, offsetDays), holidays);

/** "Start day", "+1 day", "+3 days", "+1 week", "+2 weeks". */
export function offsetLabel(days: number | null | undefined): string {
  if (days === null || days === undefined) return "No due date";
  if (days === 0) return "Start day";
  if (days % 7 === 0) return `+${days / 7} week${days === 7 ? "" : "s"}`;
  return `+${days} day${days === 1 ? "" : "s"}`;
}

/** "1 contact", "2 contacts": the word in the singular, an "s" for any other count. */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}
